import type { AgentMessage, AgentToolResultBlock, AgentToolUseBlock } from "@calcplan/ai-capabilities";
import { ensureToolResultProtocol } from "./ai-tool-protocol";

export interface ReadExplorationState {
  seenSignatures: string[];
  stagnantRounds: number;
}

export function readCallSignature(name: string, input: unknown): string {
  return `${name}:${stableStringify(input)}`;
}

export function advanceReadExploration(
  previous: ReadExplorationState,
  roundSignatures: string[],
  mutated: boolean,
): ReadExplorationState {
  if (mutated) return { seenSignatures: previous.seenSignatures, stagnantRounds: 0 };
  const seen = new Set(previous.seenSignatures);
  const hasNewRead = roundSignatures.some((signature) => !seen.has(signature));
  for (const signature of roundSignatures) seen.add(signature);
  return {
    seenSignatures: [...seen].slice(-160),
    stagnantRounds: hasNewRead ? 0 : previous.stagnantRounds + 1,
  };
}

export function advancePendingStepReadOnlyRounds(input: {
  previous: number;
  phase: "direct" | "discovery" | "planning" | "execution" | "verification";
  hasPendingStep: boolean;
  hasCalls: boolean;
  attemptedProjectMutation: boolean;
  semanticProgress: boolean;
}): number {
  if (input.semanticProgress || input.attemptedProjectMutation) return 0;
  return input.phase === "execution" && input.hasPendingStep && input.hasCalls
    ? input.previous + 1
    : input.previous;
}

export function messagesForGuidance(
  messages: AgentMessage[],
  pendingResults: AgentToolResultBlock[],
  guidanceCall: AgentToolUseBlock,
): AgentMessage[] {
  const settled = ensureToolResultProtocol([
    ...messages,
    ...(pendingResults.length ? [{ role: "user" as const, content: pendingResults }] : []),
  ]);
  return [
    ...settled,
    { role: "assistant", content: [guidanceCall] },
  ];
}

function stableStringify(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  if (value && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, item]) => `${JSON.stringify(key)}:${stableStringify(item)}`);
    return `{${entries.join(",")}}`;
  }
  return JSON.stringify(value) ?? String(value);
}
